import React from "react";
import moment from "moment-timezone";
import { LocaleUtils } from "react-day-picker";

import {
  DayPicker,
  YearMonthSelect
} from "src/library/components/DayPicker";

export interface DatePickerProps {
  date?: Date;
  setDate: (date: Date) => void;
  minimumDate?: Date;
  maximumDate?: Date;
  initialFocusDate?: Date;
}

export const DatePicker: React.ReactFCWithChildren<DatePickerProps> = ({
  date,
  setDate,
  initialFocusDate,
  minimumDate = new Date(),
  maximumDate = moment.utc().add(2, "years").toDate()
}) => {
  const [ activeDate, setActiveDate ] = React.useState<Date | undefined>(date);
  const [ month, setMonth ] = React.useState<Date>();

  const shouldDateBeDisabled = React.useCallback((day: Date): boolean => {
    if (
      day > maximumDate ||
      moment.utc(day).format("YYYY-MM-DD") < moment.utc(minimumDate).format("YYYY-MM-DD")
    ) {
      return true;
    } else {
      return false;
    }
  }, [ maximumDate, minimumDate ]);

  const modifiers = { disabled: shouldDateBeDisabled };

  const handleDateChange = React.useCallback((date: Date) => {
    if (!shouldDateBeDisabled(date)) {
      setActiveDate(date);
      setDate(date);
    }
  }, [ setDate, shouldDateBeDisabled ]);

  const handleYearMonthChange = React.useCallback((month: Date) => {
    if (minimumDate && month < minimumDate) {
      setMonth(minimumDate);
    } else if (maximumDate && month > maximumDate) {
      setMonth(maximumDate);
    } else {
      setMonth(month);
    }
  }, [ maximumDate, minimumDate ]);

  return (
    <DayPicker
      className="light"
      initialMonth={date || initialFocusDate || minimumDate || moment.utc().toDate()}
      month={month}
      firstDayOfWeek={1}
      numberOfMonths={1}
      fromMonth={minimumDate}
      toMonth={maximumDate}
      selectedDays={activeDate}
      modifiers={modifiers}
      onDayClick={handleDateChange}
      onMonthChange={setMonth}
      captionElement={({ date, localeUtils }: {date: Date; localeUtils: LocaleUtils}) => (
        <YearMonthSelect
          date={date}
          localeUtils={localeUtils}
          onChange={handleYearMonthChange}
          fromMonth={minimumDate}
          toMonth={maximumDate}
        />
      )}
    />
  );
};
